// Shared atoms for the login form steps. Visual parity with
// frontend/src/app/login/_components/shared.tsx (Tier-1, byte-for-byte JSX port).
import type { AriaRole, ReactNode } from "react";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";

interface EmailDisplayProps {
  email: string;
}

export function EmailDisplay({ email }: EmailDisplayProps): React.ReactElement {
  return (
    <div className="p-3 rounded-xl bg-brand-cyan border border-brand-cyan">
      <div className="flex items-center gap-2 text-brand-cyan text-sm">
        <Mail className="w-4 h-4" />
        <span className="font-mono">{email}</span>
      </div>
    </div>
  );
}

type FocusColor = "cyan" | "purple" | "green";

const focusColorMap: Record<FocusColor, string> = {
  cyan: "focus:border-brand-cyan focus:ring-cyan-500/20",
  purple: "focus:border-purple-400/40 focus:ring-purple-500/20",
  green: "focus:border-green-400/40 focus:ring-green-500/20",
};

const iconColorMap: Record<FocusColor, string> = {
  cyan: "group-focus-within:text-brand-cyan",
  purple: "group-focus-within:text-purple-300",
  green: "group-focus-within:text-green-300",
};

type InputMode =
  | "none"
  | "text"
  | "tel"
  | "url"
  | "email"
  | "numeric"
  | "decimal"
  | "search";

interface FormInputProps {
  type: string;
  name: string;
  autoComplete: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon: ReactNode;
  focusColor: FocusColor;
  required?: boolean;
  autoFocus?: boolean;
  minLength?: number;
  readOnly?: boolean;
  ariaLabel?: string;
  role?: AriaRole;
  // Android S8 (#android-s8-login-flow): explicit `inputMode` hints the
  // soft keyboard layout — `email` shows the @ key without forcing a full
  // type switch. Optional so existing call sites stay unchanged.
  inputMode?: InputMode;
}

export function FormInput({
  type,
  name,
  autoComplete,
  value,
  onChange,
  placeholder,
  icon,
  focusColor,
  required,
  autoFocus,
  minLength,
  readOnly,
  ariaLabel,
  role,
  inputMode,
}: FormInputProps): React.ReactElement {
  return (
    <FormField label={placeholder} name={name} required={required} srOnly>
      {(wired) => (
        <div className="group relative">
          <div
            className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40 ${iconColorMap[focusColor]} transition-colors`}
          >
            {icon}
          </div>
          <input
            {...wired}
            type={type}
            aria-label={ariaLabel}
            role={role}
            autoComplete={autoComplete}
            inputMode={inputMode}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full select-text bg-surface border border-border rounded-xl py-3.5 pl-11 pr-4 text-foreground/85 placeholder-white/55 focus:outline-none focus:ring-2 transition-all font-mono text-sm shadow-inner shadow-black/30 hover:border-border focus:bg-surface ${focusColorMap[focusColor]}`}
            autoFocus={autoFocus}
            minLength={minLength}
            readOnly={readOnly}
          />
        </div>
      )}
    </FormField>
  );
}

interface ErrorMessageProps {
  message: string;
}

export function ErrorMessage({
  message,
}: ErrorMessageProps): React.ReactElement {
  return (
    <div className="text-danger-soft-strong text-sm text-center bg-rose-500/10 border border-rose-400/30 rounded-lg py-2 px-4">
      {message}
    </div>
  );
}

interface SubmitButtonProps {
  isLoading: boolean;
  isDisabled?: boolean;
  loadingText: string;
  submitText: string;
}

export function SubmitButton({
  isLoading,
  isDisabled = false,
  loadingText,
  submitText,
}: SubmitButtonProps): React.ReactElement {
  const disabled = isLoading || isDisabled;
  return (
    <Button
      type="submit"
      data-testid="login-submit-primary"
      variant="default"
      disabled={disabled}
      // h-auto overrides global Button h-9 default; restores legacy ~52px (issue #8).
      // Fill = legacy Button variant "brand" (components/ui/button.tsx:23-26):
      // translucent cyan→indigo gradient, white text, white/10 border, blur.
      className="h-auto w-full rounded-xl border border-[color:var(--brand-cta-border)] bg-[image:var(--brand-cta-bg)] py-3.5 text-sm font-bold uppercase tracking-[0.18em] text-[color:var(--brand-cta-fg)] shadow-[var(--brand-cta-shadow)] backdrop-blur-md hover:bg-[image:var(--brand-cta-hover-bg)]"
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {loadingText}
        </span>
      ) : (
        submitText
      )}
    </Button>
  );
}

interface BackButtonProps {
  onClick: () => void;
  isDisabled?: boolean;
}

export function BackButton({
  onClick,
  isDisabled = false,
}: BackButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className="w-full py-2 rounded-md text-foreground/55 hover:text-foreground/75 text-sm transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ArrowLeft className="w-3 h-3" />
      Usar outro email
    </button>
  );
}
